import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { requestPasswordReset } from '../lib/auth';

/**
 * Forgot-password screen.
 *
 * The backend deliberately returns 200 for every well-formed request — even
 * when the address isn't registered — so an attacker can't enumerate users.
 * We mirror that here: after submit, we *always* show the "check your email"
 * confirmation, regardless of what (if anything) the server actually did.
 */
export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    if (pending || submitted) return;
    setPending(true);
    await requestPasswordReset(email.trim());
    setPending(false);
    setSubmitted(true);
  }

  return (
    <div className="min-h-screen relative grid place-items-center px-5 overflow-hidden">
      <div aria-hidden="true" className="absolute inset-0  opacity-80" />
      <div aria-hidden="true" className="absolute inset-x-0 top-1/4 h-[400px] " />

      <div className="relative w-full max-w-[420px]">
        <div className="flex items-center justify-center gap-2 mb-6">
          <span className="w-8 h-8 rounded-md bg-ink text-paper grid place-items-center font-extrabold text-[15px] leading-none" aria-hidden="true">T</span>
          <span className="font-bold tracking-tight text-[20px] text-ink">Thren</span>
        </div>

        <div className="surface p-7">
          {submitted ? (
            <>
              <h1 className="text-[22px] font-bold tracking-tight">Check your email</h1>
              <p className="mt-2 text-[14px] text-muted leading-relaxed">
                If an account exists for{' '}
                <span className="text-ink">{email}</span>, we just sent a
                password-reset link. It expires in an hour — open it on the
                same device you signed up on if you can.
              </p>
              <div className="mt-6 flex items-center justify-between text-[12.5px]">
                <Link to="/login" className="text-accent hover:underline">
                  Back to sign in
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setSubmitted(false);
                    setEmail('');
                  }}
                  className="btn-ghost px-0 hover:bg-transparent text-muted hover:text-ink"
                >
                  Use a different email
                </button>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-[22px] font-bold tracking-tight">Reset your password</h1>
              <p className="mt-2 text-[14px] text-muted leading-relaxed">
                Enter the email you signed up with and we&rsquo;ll send a
                reset link.
              </p>

              <form onSubmit={handleSubmit} className="mt-5 space-y-3.5" noValidate>
                <label htmlFor="email" className="block">
                  <span className="block text-[11.5px] uppercase tracking-wider text-muted font-semibold mb-1.5">
                    Email
                  </span>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    disabled={pending}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-md bg-paper2 border border-rule px-3 py-2.5 text-[14px] text-ink placeholder:text-muted/45 outline-none transition-colors focus:border-electric-500/60 focus:bg-paper2 disabled:opacity-60"
                  />
                </label>

                <button
                  type="submit"
                  disabled={pending || !email}
                  className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {pending ? 'Sending…' : 'Send reset link'}
                </button>

                <p className="text-center text-[12.5px] text-muted pt-1">
                  Remembered it?{' '}
                  <Link to="/login" className="text-accent hover:underline">
                    Sign in
                  </Link>
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
